/**
 * Header auto-hide on scroll.
 *
 * Scroll DOWN past the header height → hide the header (translate -100%).
 * Scroll UP → reveal it again.
 *
 * Also keeps a `--header-offset` CSS variable on <html> in sync with the
 * header's current visible height. The sticky ATC binds its `top` to that
 * variable so it pins to either right below the header or to the screen
 * top, depending on whether the header is visible.
 *
 * Runs vanilla (no custom-element registration) so it can attach to the
 * theme's plain <header class="hhs-header"> tag without re-architecting it.
 */

const HEADER_SELECTOR = "header.hhs-header";
const HIDDEN_CLASS = "is-hidden";
const SCROLLED_CLASS = "is-scrolled";
const SCROLLED_THRESHOLD = 8; // px scrolled before the frosted state kicks in
const TOP_BUFFER = 80; // px from top before we ever hide
const DELTA = 6; // ignore micro-jitter
const HEADER_VAR = "--header-offset";

let header = null;
let lastY = 0;
let lastDir = "up";
let ticking = false;
let measuredHeight = 0;
let ro = null;

function measure() {
  if (!header) return;
  const rect = header.getBoundingClientRect();
  measuredHeight = Math.round(rect.height);
  if (!header.classList.contains(HIDDEN_CLASS)) {
    document.documentElement.style.setProperty(
      HEADER_VAR,
      `${measuredHeight}px`,
    );
  }
}

function applyHidden(hidden) {
  if (!header) return;
  if (hidden === header.classList.contains(HIDDEN_CLASS)) return;
  header.classList.toggle(HIDDEN_CLASS, hidden);
  document.documentElement.style.setProperty(
    HEADER_VAR,
    hidden ? "0px" : `${measuredHeight}px`,
  );
}

function applyScrolled(scrolled) {
  if (!header) return;
  if (scrolled === header.classList.contains(SCROLLED_CLASS)) return;
  header.classList.toggle(SCROLLED_CLASS, scrolled);
}

function onScroll() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => {
    const y = window.scrollY || document.documentElement.scrollTop || 0;
    applyScrolled(y > SCROLLED_THRESHOLD);
    const dy = y - lastY;

    if (Math.abs(dy) > DELTA) {
      const dir = dy > 0 ? "down" : "up";

      if (y <= TOP_BUFFER) {
        applyHidden(false);
      } else if (dir === "down" && lastDir !== "down") {
        applyHidden(true);
        lastDir = "down";
      } else if (dir === "up" && lastDir !== "up") {
        applyHidden(false);
        lastDir = "up";
      } else if (dir === "down") {
        applyHidden(true);
      } else {
        applyHidden(false);
      }

      lastY = y;
    }

    ticking = false;
  });
}

function init() {
  header = document.querySelector(HEADER_SELECTOR);
  if (!header) return;
  measure();
  lastY = window.scrollY || 0;
  applyScrolled(lastY > SCROLLED_THRESHOLD);

  ro = new ResizeObserver(measure);
  ro.observe(header);

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", measure, { passive: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
